
// src/components/landing/ContactForm.tsx
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Send } from "lucide-react";
import type { Dictionary } from '@/lib/i18n';

interface ContactFormProps {
  dictionary: Dictionary['contact'];
}

const contactFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  subject: z.string().min(5, { message: "Subject must be at least 5 characters." }),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }).max(500, { message: "Message cannot exceed 500 characters." }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export function ContactForm({ dictionary }: ContactFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
  });

  async function onSubmit(data: ContactFormValues) {
    setIsSubmitting(true);
    console.log("Contact Form Submitted:", data);
    await new Promise(resolve => setTimeout(resolve, 1500));

    toast({
      title: dictionary.messageSentTitle,
      description: dictionary.messageSentDesc,
    });
    reset();
    setIsSubmitting(false);
  }

  return (
    <Card className="w-full shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl">{dictionary.formTitle}</CardTitle>
        <CardDescription>{dictionary.formSubtitle}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <Label htmlFor="name">{dictionary.nameLabel}</Label>
              <Input id="name" placeholder={dictionary.namePlaceholder} {...register("name")} />
              {errors.name && <p className="text-sm text-destructive mt-1">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">{dictionary.emailLabel}</Label>
              <Input id="email" type="email" placeholder={dictionary.emailPlaceholder} {...register("email")} />
              {errors.email && <p className="text-sm text-destructive mt-1">{errors.email.message}</p>}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="subject">{dictionary.subjectLabel}</Label>
            <Input id="subject" placeholder={dictionary.subjectPlaceholder} {...register("subject")} />
            {errors.subject && <p className="text-sm text-destructive mt-1">{errors.subject.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="message">{dictionary.messageLabel}</Label>
            <Textarea
              id="message"
              placeholder={dictionary.messagePlaceholder}
              {...register("message")}
              rows={5}
            />
            {errors.message && <p className="text-sm text-destructive mt-1">{errors.message.message}</p>}
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {dictionary.sendingButton}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {dictionary.sendButton}
              </>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

    